import { z } from 'zod';

// Wrapper type with conditional optional
type WithConditionalOptional<K extends z.ZodTypeAny> = K & {
  optional<T extends boolean>(flag: T): T extends true ? z.ZodOptional<K> : K;
};

// Helper to wrap schemas
function wrap<K extends z.ZodTypeAny>(schema: K): WithConditionalOptional<K> {
  return Object.assign(schema, {
    optional<T extends boolean>(
      flag: T
    ): T extends true ? z.ZodOptional<K> : K {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (flag ? (z.optional(schema) as any) : schema) as any;
    },
  });
}

export const zo = {
  boolean: () => wrap(z.boolean()),
  string: () => wrap(z.string()),
  number: () => wrap(z.number()),
  date: () => wrap(z.date()),
  array: <T extends z.ZodTypeAny>(item: T) => wrap(z.array(item)),
  enum: <T extends string, U extends [T, ...T[]]>(values: U) =>
    wrap(z.enum(values)),
  object: <T extends z.ZodRawShape>(shape: T) => wrap(z.object(shape)),
  union: <T extends [z.ZodTypeAny, ...z.ZodTypeAny[]]>(types: T) =>
    wrap(z.union(types)),
  function: <A extends z.ZodTypeAny, R extends z.ZodTypeAny>(
    _args: A,
    _returns: R
  ) =>
    wrap(
      z.custom<(arg: z.infer<A>) => z.infer<R>>(
        (val): val is (arg: z.infer<A>) => z.infer<R> =>
          typeof val === 'function'
      )
    ),
};
